/**
 * Dark / light mode.
 *
 * Three states, not two: 'light', 'dark', and 'system' (the default), which
 * follows the phone and keeps following it when the phone switches at sunset.
 * The choice is stored per device — the counter phone and a laptop can differ.
 *
 * The same logic runs inline in index.html before first paint; if this changes,
 * change that too.
 */
import { useCallback, useEffect, useState } from 'react';

const STORAGE_KEY = 'vj-theme';
const media = () => window.matchMedia('(prefers-color-scheme: dark)');

function readPreference() {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored === 'dark' || stored === 'light' ? stored : 'system';
  } catch {
    return 'system';
  }
}

function resolve(preference) {
  return preference === 'system' ? (media().matches ? 'dark' : 'light') : preference;
}

function apply(preference) {
  document.documentElement.classList.toggle('dark', resolve(preference) === 'dark');
}

export function useTheme() {
  const [preference, setPreference] = useState(readPreference);

  useEffect(() => {
    apply(preference);
    try {
      if (preference === 'system') localStorage.removeItem(STORAGE_KEY);
      else localStorage.setItem(STORAGE_KEY, preference);
    } catch {
      /* private browsing — the choice simply won't persist */
    }
  }, [preference]);

  // Keep up with the system while the app is open, not just at load.
  useEffect(() => {
    if (preference !== 'system') return undefined;
    const mq = media();
    const onChange = () => apply('system');
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, [preference]);

  const toggle = useCallback(() => {
    setPreference((current) => (resolve(current) === 'dark' ? 'light' : 'dark'));
  }, []);

  return { preference, resolved: resolve(preference), setPreference, toggle };
}
